import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { SearchResult } from './types';
import ListingCard, { ListingCardSkeleton } from '../ListingCard';
import { ListingCardListing } from '../ListingCard/types';
import { listingShareUrl } from '../utils/listingSlug';
import { shareUrlWithFallback } from '../../utils/shareUtils';
import LoadingSpinner from '../common/LoadingSpinner';
import { CloseIcon } from '../common/CloseIcon';
import { getCookie } from '../utils/utils';
import RemoveSellReasonModal from '../common/RemoveSellReasonModal';
import './SearchResults.scss';
import '../MessagesModal/MessagesModal.scss';
import { unconfirmedDetailsLabel } from './matchDetails';
import LoadingLabel from '../common/LoadingLabel';

interface SearchResultsProps {
  results: SearchResult[];
  secondaryResults?: SearchResult[];
  hasDetailFilters?: boolean;
  onViewOnMap: (result: SearchResult) => void;
  onLoadMore: () => void;
  isLoadingMore: boolean;
  hasMore: boolean;
  totalResults: number;
  secondaryTotalResults?: number;
  noFiltersSet: boolean;
  onScroll?: (scrollTop: number) => void;
  /**
   * Bumped by the parent each time a fresh (non-append) result set lands, i.e.
   * after every settled map move or address search. Returns the list to the top
   * so the viewer isn't left scrolled into pages that no longer exist.
   */
  resetKey?: number;
  showHeaders?: boolean;
  setShowRemoveListingDialog: (listingId: string | null) => void;
  showRemoveListingDialog: string | null;
  onRemoveResult: (listingId: string) => void;
  performSearch: (loadMore?: boolean) => void;
  isAdmin?: boolean;
  isLoggedIn?: boolean;
  setLoginModalVisible?: (visible: boolean) => void;
  onSelectBuilding: (buildingId: string) => void;
  countryOfFeedContent: string;
  onOpenListing?: (result: SearchResult) => void;
  onHoverListing?: (buildingId: string | null) => void;
  /**
   * Opt-in: renders the seller card's "Could buy" pill. Buyers ignore it (they
   * carry their own Suggest/Share footer). Only surfaces that wire this — the
   * landing explore panel — get the pill, so other SearchResults uses are
   * untouched. The handler routes to the building page with the buy modal
   * auto-opened and the clicked listing as target, ending in that seller's
   * direct message (JAA-275).
   */
  onCouldBuy?: (result: SearchResult) => void;
  /** Renders a non-listing row (e.g. an area-demand buyer card). The backend
   *  merges these into `results` already distance-sorted, so SearchResults just
   *  delegates their rendering and stays agnostic about the card type. */
  renderExtraCard?: (result: SearchResult) => React.ReactNode;
}

/** The explore listing grid: cards, empty states and infinite scroll. */
const SearchResults: React.FC<SearchResultsProps> = ({
  results,
  secondaryResults = [],
  hasDetailFilters = false,
  onViewOnMap,
  onLoadMore,
  isLoadingMore,
  hasMore,
  totalResults,
  secondaryTotalResults = 0,
  noFiltersSet,
  onScroll,
  setShowRemoveListingDialog,
  showHeaders = true,
  showRemoveListingDialog,
  onRemoveResult,
  performSearch,
  isAdmin,
  isLoggedIn,
  setLoginModalVisible,
  onSelectBuilding,
  countryOfFeedContent,
  onOpenListing,
  onHoverListing,
  onCouldBuy,
  renderExtraCard,
  resetKey,
}) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  /**
   * Return the list to the top on every fresh result set. Which element scrolls
   * differs by layout — the results div on desktop, the info sheet on mobile —
   * so walk up to the first genuinely scrollable ancestor rather than assuming,
   * the same asymmetry that made an onScroll handler here unreliable.
   */
  useEffect(() => {
    if (resetKey === undefined) return;
    let el: HTMLElement | null = rootRef.current;
    while (el) {
      const overflowY = window.getComputedStyle(el).overflowY;
      const scrolls = overflowY === 'auto' || overflowY === 'scroll';
      if (scrolls && el.scrollHeight > el.clientHeight) {
        el.scrollTop = 0;
        return;
      }
      el = el.parentElement;
    }
  }, [resetKey]);

  const removingListing = showRemoveListingDialog
    ? [...results, ...secondaryResults].find(r => r._id === showRemoveListingDialog)
    : null;
  const isRemovingSellListing = removingListing?.could_sell ?? false;

  const handleDelete = async (removalReason?: string, removalReasonText?: string) => {
    if (!showRemoveListingDialog) return;

    try {
      setIsDeleting(true);
      const fetchOptions: RequestInit = {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'X-CSRF-TOKEN': getCookie('csrf_access_token'),
        },
      };
      if (removalReason) {
        (fetchOptions.headers as Record<string, string>)['Content-Type'] = 'application/json';
        const body: Record<string, string> = { removal_reason: removalReason };
        if (removalReasonText) body.removal_reason_text = removalReasonText;
        fetchOptions.body = JSON.stringify(body);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/off-market-willingness/${showRemoveListingDialog}`,
        fetchOptions
      );

      if (!response.ok) {
        throw new Error('Failed to delete');
      }

      // Remove from local state instead of reloading page
      onRemoveResult(showRemoveListingDialog);
      setShowRemoveListingDialog(null);
    } catch (err) {
      console.error('Error deleting willingness:', err);
      alert(t('common.error', 'An error occurred. Please try again.'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    onScroll?.(e.currentTarget.scrollTop);
  }, [onScroll]);

  const allResults = [...results, ...secondaryResults];

  /**
   * Infinite-scroll trigger: loads the next page once the end-of-list sentinel
   * comes near the viewport. Which element actually scrolls differs per surface
   * (this div on desktop, the mobile info sheet on mobile) and React's onScroll
   * does not bubble, so a scroll handler here never fired on mobile (JAA-312).
   * An observer against the viewport is scroller-agnostic. Re-running on
   * `allResults.length` re-arms it when a page lands without filling the view.
   */
  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !hasMore || isLoadingMore) return undefined;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          // Unobserve first so one pass can't fire two requests for one page.
          io.unobserve(sentinel);
          onLoadMore();
        }
      },
      { rootMargin: '200px' },
    );
    io.observe(sentinel);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isLoadingMore, onLoadMore, allResults.length]);

  const showSeparator = hasDetailFilters && secondaryResults.length > 0;

  // Skeletons ride inside the last grid rather than in a grid of their own, so
  // the grid's own `gap` spaces them off the last card. A sibling grid would
  // instead inherit that grid's larger bottom padding (40px on desktop).
  const loadingSkeletons = isLoadingMore ? <ListingCardSkeleton count={2} /> : null;

  const renderCard = (result: SearchResult, keyPrefix = '') => {
    const isSeller = !(result.type === 'area_demand' || (result.could_buy && !result.could_sell));
    const note = result.match_status === 'unconfirmed'
      ? unconfirmedDetailsLabel(result.unconfirmed_fields, t, isSeller ? 'seller' : 'buyer')
      : '';
    const withNote = (card: React.ReactNode) => note ? (
      <div key={`${keyPrefix}${result._id}`} className="search-results__possible-card">
        {card}
        <p className="search-results__possible-note">{note}</p>
      </div>
    ) : <React.Fragment key={`${keyPrefix}${result._id}`}>{card}</React.Fragment>;
    // Area-demand rows are merged into results by the backend, already distance
    // sorted; delegate their rendering to the host (as a buyer card).
    if (result.type === 'area_demand' && renderExtraCard) {
      return withNote(renderExtraCard(result));
    }
    return withNote(
      <ListingCard
        size="big"
        type={isSeller ? 'seller' : 'buyer'}
        listing={result as unknown as ListingCardListing}
        onOpen={() => (onOpenListing ? onOpenListing(result) : onViewOnMap(result))}
        onMouseEnter={() => onHoverListing?.(result.building_id)}
        onMouseLeave={() => onHoverListing?.(null)}
        isLoggedIn={isLoggedIn}
        setLoginModalVisible={setLoginModalVisible}
        onShare={
          isSeller
            ? () => shareUrlWithFallback({ url: listingShareUrl(result._id), t })
            : undefined
        }
        onToggleLike={() => {
          if (!isLoggedIn) setLoginModalVisible?.(true);
        }}
        onToggleCouldBuy={
          isSeller && onCouldBuy ? () => onCouldBuy(result) : undefined
        }
      />
    );
  };

  if (allResults.length === 0) {
    return (
      <div className="search-results search-results--empty">
        <div className="search-results__empty-state">
          <svg className="search-results__empty-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"></path>
          </svg>
          <h3 className="search-results__empty-title">{t('exploreListings.noResultsTitle')}</h3>
          <p className="search-results__empty-description">{t('exploreListings.noResultsDescription')}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className={`search-results ${!showHeaders ? 'search-results--headers-hidden' : ''}`} onScroll={handleScroll}>
      {secondaryTotalResults > 0 && hasDetailFilters && (
        <p className="search-results__possible-count">
          {t('exploreListings.unconfirmedCount', { count: secondaryTotalResults })}
        </p>
      )}
{/*       {noFiltersSet && (
        <MobileRecommendedProperties
          onSelectBuilding={onSelectBuilding}
          countryOfFeedContent={countryOfFeedContent}
        />
      )} */}
      <div className={`search-results__grid ${secondaryResults.length > 0 ? 'search-results__grid--also-secondary-results' : ''}`}>
        {results.map(result => renderCard(result))}
        {results.length === 0 && <div className="search-results__secondary-empty-state">
          <h3 className="search-results__empty-title">{t('exploreListings.noConfirmedMatches')}</h3>
          <p className="search-results__empty-description">{t('exploreListings.noConfirmedMatchesDescription')}</p>
        </div>}
        {secondaryResults.length === 0 && loadingSkeletons}
      </div>

      {showSeparator && (
        <div className="search-results__separator">
          <div className="search-results__separator-line"></div>
          <div className="search-results__separator-text">
            <strong>{t('exploreListings.unconfirmedTitle')}</strong>
            <span>{t('exploreListings.unconfirmedDescription')}</span>
          </div>
          <div className="search-results__separator-line"></div>
        </div>
      )}

      {secondaryResults.length > 0 && (
        <div className="search-results__grid search-results__grid--secondary">
          {secondaryResults.map(result => renderCard(result, 'secondary-'))}
          {loadingSkeletons}
        </div>
      )}

      {hasMore && (
        <div
          ref={loadMoreRef}
          className="search-results__load-more-sentinel"
          aria-hidden="true"
        />
      )}

      {/*       {!hasMore && results.length > 0 && (
        <div className="search-results__end-message">
          {t('exploreListings.endOfResults')}
        </div>
      )} */}

      {/* Removal modal: reason modal for sell listings, simple confirm for buy listings */}
      {showRemoveListingDialog && isRemovingSellListing && (
        <RemoveSellReasonModal
          isOpen={true}
          isRemoving={isDeleting}
          onClose={() => setShowRemoveListingDialog(null)}
          onConfirm={(reason, reasonText) => handleDelete(reason, reasonText)}
        />
      )}
      {showRemoveListingDialog && !isRemovingSellListing && (
        <div className="end-chat-confirmation-overlay" onClick={() => !isDeleting && setShowRemoveListingDialog(null)}>
          <div className="end-chat-confirmation" onClick={(e) => e.stopPropagation()}>
            <div className="end-chat-confirmation__header">
              <h3>
                <Trans i18nKey="building.offMarket.modal.deleteConfirmTitle">Remove listing</Trans>
              </h3>
              <button
                className="end-chat-confirmation__close"
                onClick={() => setShowRemoveListingDialog(null)}
                disabled={isDeleting}
              >
                <CloseIcon color="#202125" size={16} />
              </button>
            </div>
            <div className="end-chat-confirmation__content">
              <p>
                <Trans i18nKey="building.offMarket.modal.deleteConfirmText">Are you sure you want to remove this listing?</Trans>
              </p>
            </div>
            <div className="end-chat-confirmation__actions">
              <button
                className="end-chat-confirmation__button end-chat-confirmation__button--cancel"
                onClick={() => setShowRemoveListingDialog(null)}
                disabled={isDeleting}
              >
                <Trans i18nKey="building.offMarket.modal.cancel">Cancel</Trans>
              </button>
              <button
                className="end-chat-confirmation__button end-chat-confirmation__button--confirm"
                onClick={() => handleDelete()}
                disabled={isDeleting}
              >
                <LoadingLabel loading={isDeleting} loadingContent={t('building.offMarket.modal.loading', 'Loading...')}>
                  <Trans i18nKey="building.offMarket.modal.delete">Remove</Trans>
                </LoadingLabel>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResults;
